import { useState } from "react";
import { useParams } from "react-router-dom";
import villas from "../../../constants/villas.json";

import {
  Box,
  Container,
  Typography,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Star,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AllImagesModal from "../../../components/AllImagesModal";

const PropertyHero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllImagesModal, setShowAllImagesModal] = useState(false);
  const { id } = useParams();
  const villa = villas.find((v) => String(v.id) === String(id));
  // Use images array if present, else fallback to single image, else empty array
  const propertyImages = villa.images;
  const additionalImages = villa?.additionalImages || 0;

  const handlePrevImage = () => {
    setCurrentImageIndex(
      currentImageIndex === 0
        ? propertyImages.length - 1
        : currentImageIndex - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex(
      currentImageIndex === propertyImages.length - 1
        ? 0
        : currentImageIndex + 1
    );
  };

  const handleBackClick = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/properties");
    }
  };

  return (
    <Box sx={{ py: 3, mb: 4 }}>
      <Container maxWidth="xl" sx={{ px: 2 }}>
        {/* Back Button */}
        <Box sx={{ mb: 3 }}>
          <IconButton
            onClick={handleBackClick}
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              width: 48,
              height: 48,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        </Box>

        {/* Header Section with Title and Rating */}
        <Box
          data-aos="fade-up"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 4,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Property Info */}
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: "Monument-Private",
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.5rem" },
                mb: 1,
                color: "text.primary",
              }}
            >
              {villa?.name || "Property"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                color: "text.secondary",
              }}
            >
              {villa?.guests ? `${villa.guests} guests | ` : ""}
              {villa?.bedrooms ? `${villa.bedrooms} bedrooms | ` : ""}
              {villa?.bathrooms ? `${villa.bathrooms} bathrooms` : ""}
            </Typography>
          </Box>

          {/* Right Section: Rating */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              px: 2,
              py: 1,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                width: "1.2rem",
                height: "1.2rem",
                backgroundImage: "url(/star.svg)",
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Monument-Private",
                fontWeight: 700,
                fontSize: "1.5rem",
              }}
            >
              4.8
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.9rem",
              }}
            >
              /5
            </Typography>
          </Box>
        </Box>

        {/* Image Gallery Section */}
        <Grid
          container
          spacing={2}
          sx={{ height: "70vh", minHeight: "500px" }}
          data-aos="fade-up"
          data-aos-delay="150"
        >
          {/* Main Large Image - Static on Desktop, Slideshow on Mobile */}
          <Grid item size={{ xs: 12, md: 6 }} sx={{ height: "100%" }}>
            <Box
              sx={{
                position: "relative",
                height: "100%",
                borderRadius: 3,
                overflow: "hidden",
                backgroundImage: {
                  xs: `url(${propertyImages[currentImageIndex]})`, // Slideshow on mobile
                  md: `url(${propertyImages[0]})`, // Static image on desktop
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                minHeight: "400px",
                transition: "transform 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
              onClick={() => setShowAllImagesModal(true)}
            >
              {/* Navigation Arrows - Only visible on mobile */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage();
                  }}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>

                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Grid>

          {/* Right Column - First Big Image and Small Thumbnails - Only visible on desktop */}
          <Grid
            item
            size={{ xs: 12, md: 6 }}
            sx={{
              height: "100%",
              display: { xs: "none", md: "block" }, // Hide on mobile, show on desktop
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
              }}
            >
              {/* Desktop Image Grid using images from JSON */}
              {propertyImages && propertyImages.length > 0 && (
                <>
                  {/* First Big Image */}
                  <Box
                    sx={{
                      flex: 2,
                      borderRadius: 3,
                      overflow: "hidden",
                      backgroundImage: `url(${propertyImages[1]})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                    onClick={() => setShowAllImagesModal(true)}
                  />
                  {/* Thumbnails Row */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    {/* Show up to 2 more thumbnails, then overlay for remaining */}
                    {propertyImages.slice(2, 3).map((img, idx) => (
                      <Box
                        key={img}
                        sx={{
                          flex: 1,
                          borderRadius: 3,
                          overflow: "hidden",
                          backgroundImage: `url(${img})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          cursor: "pointer",
                          transition: "transform 0.3s ease",
                          position: "relative",
                          "&:hover": {
                            transform: "scale(1.02)",
                          },
                        }}
                        onClick={() => setShowAllImagesModal(true)}
                      />
                    ))}
                    {/* If more images, show overlay on last thumbnail */}
                    {propertyImages.length > 3 && (
                      <Box
                        sx={{
                          flex: 1,
                          borderRadius: 3,
                          overflow: "hidden",
                          backgroundImage: `url(${propertyImages[3]})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          cursor: "pointer",
                          position: "relative",
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.02)",
                          },
                        }}
                        onClick={() => setShowAllImagesModal(true)}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontFamily: "Monument-Private",
                              fontWeight: 600,
                              fontSize: "1.2rem",
                            }}
                          >
                            +{propertyImages.length - 3} more
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
      {/* Modal for all images (with slideshow) */}
      <AllImagesModal
        open={showAllImagesModal}
        onClose={() => setShowAllImagesModal(false)}
        images={propertyImages}
      />
    </Box>
  );
};

export default PropertyHero;
